import { useState, useEffect } from 'react';
import { Layout } from '@/shared/components/Layout';
import { api } from '@/shared/lib/api';
import { captureError } from '@/shared/lib/monitoring';

interface DailyStat {
  date: string;
  answered_cards: number;
  correct_answers: number;
  total_answers: number;
  xp_gained: number;
  accuracy: number;
}

interface Summary {
  total_answered: number;
  total_correct: number;
  total_answers: number;
  overall_accuracy: number;
  total_xp: number;
  days_studied: number;
  current_streak: number;
  character: { level: number; xp: number; name: string } | null;
  word_count: { total: number; in_srs: number; due_now: number };
}

interface Today {
  date: string;
  answered_cards: number;
  correct_answers: number;
  total_answers: number;
  xp_gained: number;
  accuracy: number;
  word_count: { total: number; in_srs: number; due_now: number };
}

function StatCard({ value, label, color = 'var(--theme-primary)' }: {
  value: string | number;
  label: string;
  color?: string;
}) {
  return (
    <div style={{
      background: 'var(--theme-bg-card)',
      border: '1px solid var(--theme-border)',
      borderRadius: 'var(--theme-border-radius)',
      padding: '16px',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', marginTop: 4 }}>{label}</div>
    </div>
  );
}

function MiniBarChart({ data }: { data: DailyStat[] }) {
  if (!data.length) return null;
  const maxCards = Math.max(...data.map(d => d.answered_cards), 1);
  const last14 = data.slice(-14);

  return (
    <div style={{
      background: 'var(--theme-bg-card)',
      border: '1px solid var(--theme-border)',
      borderRadius: 'var(--theme-border-radius)',
      padding: '16px',
      marginBottom: 16,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--theme-text)', marginBottom: 12 }}>
        Карточек за 14 дней
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 64 }}>
        {last14.map((d, i) => {
          const height = Math.max(4, (d.answered_cards / maxCards) * 64);
          const isToday = d.date === new Date().toISOString().split('T')[0];
          return (
            <div
              key={i}
              title={`${d.date}: ${d.answered_cards} карточек`}
              style={{
                flex: 1,
                height,
                background: isToday ? 'var(--theme-primary)' : 'var(--theme-primary)66',
                borderRadius: '3px 3px 0 0',
                minWidth: 0,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

export function StatsPage() {
  const [today, setToday] = useState<Today | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [history, setHistory] = useState<DailyStat[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get<Today>('/stats/daily'),
      api.get<Summary>('/stats/summary'),
      api.get<DailyStat[]>('/stats/history?days=30'),
    ])
      .then(([todayData, summaryData, historyData]) => {
        setToday(todayData);
        setSummary(summaryData);
        setHistory(Array.isArray(historyData) ? historyData : []);
      })
      .catch((err) => {
        console.error(err);
        captureError(err, { context: 'StatsPage.loadStats' });
      })
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return (
      <Layout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--theme-text-secondary)' }}>
          Загрузка...
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{ padding: '20px 16px' }}>
        <h1 style={{ margin: '0 0 20px', fontSize: 22, color: 'var(--theme-text)' }}>Статистика</h1>

        {/* Стрик и персонаж */}
        {summary && (
          <div style={{
            background: 'var(--theme-bg-card)',
            border: '1px solid var(--theme-border)',
            borderRadius: 'var(--theme-border-radius)',
            padding: '20px 16px',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}>
            <div style={{ fontSize: 48 }}>
              {summary.current_streak >= 7 ? '🔥' : summary.current_streak >= 3 ? '⚡' : '📚'}
            </div>
            <div>
              <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--theme-primary)' }}>
                {summary.current_streak} {summary.current_streak === 1 ? 'день' : summary.current_streak < 5 ? 'дня' : 'дней'}
              </div>
              <div style={{ fontSize: 13, color: 'var(--theme-text-secondary)' }}>Стрик занятий</div>
              {summary.character && (
                <div style={{ fontSize: 12, color: 'var(--theme-text-muted)', marginTop: 4 }}>
                  {summary.character.name} · Уровень {summary.character.level}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Сегодня */}
        {today && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 10 }}>
              Сегодня
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
              <StatCard value={today.answered_cards} label="Карточек" />
              <StatCard value={`${today.accuracy}%`} label="Точность" color="var(--theme-success)" />
              <StatCard value={`+${today.xp_gained}`} label="XP" color="var(--theme-warning)" />
            </div>
          </>
        )}

        {/* График */}
        <MiniBarChart data={history} />

        {/* Слова */}
        {today && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 10 }}>
              Слова
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 16 }}>
              <StatCard value={today.word_count.total} label="Всего" />
              <StatCard value={today.word_count.in_srs} label="В SRS" color="var(--mode-srs)" />
              <StatCard value={today.word_count.due_now} label="К повтору" color="var(--theme-error)" />
            </div>
          </>
        )}

        {/* Всего за всё время */}
        {summary && (
          <>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--theme-text-muted)', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 10 }}>
              За всё время
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 24 }}>
              <StatCard value={summary.total_answered} label="Всего карточек" />
              <StatCard value={`${summary.overall_accuracy}%`} label="Точность" color="var(--theme-success)" />
              <StatCard value={summary.total_xp} label="Всего XP" color="var(--theme-warning)" />
              <StatCard value={summary.days_studied} label="Дней занятий" color="var(--theme-primary)" />
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
